import { IN_STOCK } from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { RingSizeData } from '@jouzen/ecom-components/v3/types/ringSizeData';
import { useEffect, useMemo, useReducer, useRef } from 'react';

import type { CurrentRouteValues } from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useCurrentRoute';
import useMultiRingSelections from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useMultiRingSelections';
import type { Order } from '@/app/[locale]/product/_components/HardwarePDP/_types/order';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import SizeSelection from '@/app/[locale]/product/_steps/enums/SizeStep';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import { readFromSessionStorage } from '@/utils/sessionStorage';

import {
  CURRENT_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  INITIAL_STATE,
  ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  ONE_YEAR_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
  reducer,
  SIZE_SELECTED_SUMMARY_KEY,
  SIZING_KIT_SUMMARY_KEY,
  THREE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
  TWO_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
} from './buildOrderReducer';

export interface ParsedState {
  order: Order;
}

export type RadioValue =
  | SizeSelection
  | MembershipSelection
  | WarrantySelection;

export interface UseBuildRingOrderProps {
  currentRoute: CurrentRouteValues;
  isAddingToCart: boolean;
  oneMonthMembershipSku: string;
  oneYearMembershipSku: string;
  ringSizesData: RingSizeData[] | undefined;
  sessionStorageKey: string;
  threeYearWarrantySku: string;
  twoYearWarrantySku: string;
}

interface UseBuildRingOrderState {
  isSelectingSize: boolean;
  onFinishChange: (value: string) => void;
  onRadioChange: (value: string) => void;
  onSizeChange: (value: string) => void;
  order: Order;
}

const useBuildRingOrder = ({
  currentRoute,
  isAddingToCart,
  oneMonthMembershipSku,
  oneYearMembershipSku,
  ringSizesData,
  sessionStorageKey,
  threeYearWarrantySku,
  twoYearWarrantySku,
}: UseBuildRingOrderProps): UseBuildRingOrderState => {
  const [{ isSelectingSize, order }, dispatch] = useReducer(
    reducer(sessionStorageKey),
    INITIAL_STATE,
  );
  const { membershipSelection, warrantySelection } = useMultiRingSelections();

  const finish = currentRoute.finish.value;
  const selectedSku = useRef<string>(order.product.sku);

  const ringSizesDataBySize: Record<string, RingSizeData> | undefined =
    useMemo(() => {
      return ringSizesData?.reduce((acc, cur) => {
        const key = !isNaN(Number(cur.size))
          ? cur.size
          : SizeSelection.SizingKit;
        return {
          ...acc,
          [key]: cur,
        };
      }, {});
    }, [ringSizesData]);

  const sizeLaterData = ringSizesDataBySize?.[SizeSelection.SizingKit];

  // Set initial order state
  useEffect(() => {
    if (!isAddingToCart) {
      // Prevent potential to set initial order state more than once
      const storedState = readFromSessionStorage(sessionStorageKey);
      if (storedState) {
        const parsedState = JSON.parse(storedState) as ParsedState;
        dispatch({ type: 'SET_ORDER', payload: parsedState.order });
      } else {
        dispatch({
          type: 'SET_ORDER',
          payload: {
            finish: {
              value: finish,
            },
            size: {
              value: SizeSelection.SizingKit,
              summaryKey: SIZING_KIT_SUMMARY_KEY,
            },
            product: {
              sku: sizeLaterData?.sku ?? '',
              stockStatus: IN_STOCK,
            },
            membership: membershipSelection,
            warranty: warrantySelection,
            ready: true,
          },
        });
      }
    }
  }, [
    finish,
    sizeLaterData?.sku,
    membershipSelection,
    warrantySelection,
    isAddingToCart,
    sessionStorageKey,
  ]);

  // Update order product data when size/finish changes take place
  // (this is necessary for finishes because ringSizeData updates
  // after we update the order.finish value).
  useEffect(() => {
    const selectedRingSizeData = ringSizesDataBySize?.[order.size.value];
    // Prevent unecessary updates
    if (
      selectedRingSizeData?.sku &&
      selectedRingSizeData.sku !== selectedSku.current
    ) {
      dispatch({
        type: 'UPDATE_PRODUCT',
        payload: {
          sku: selectedRingSizeData.sku,
          stockStatus: selectedRingSizeData.stockStatus ?? IN_STOCK,
        },
      });
      selectedSku.current = selectedRingSizeData.sku;
    }
  }, [order, ringSizesDataBySize]);

  const handleRadioChange = (value: string): void => {
    const _val = value as RadioValue;
    if (Object.values(SizeSelection).includes(value as SizeSelection)) {
      if (_val === SizeSelection.SizingKit) {
        dispatch({
          type: 'UPDATE_SIZE',
          payload: {
            value: SizeSelection.SizingKit,
            summaryKey: SIZING_KIT_SUMMARY_KEY,
          },
        });
      } else {
        dispatch({ type: 'SELECTING_SIZE' });
      }
    } else if (
      Object.values(MembershipSelection).includes(value as MembershipSelection)
    ) {
      let sku = '';
      let summaryKey = ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      if (_val === MembershipSelection.OneMonth) {
        sku = oneMonthMembershipSku;
      } else if (_val === MembershipSelection.OneYear) {
        sku = oneYearMembershipSku;
        summaryKey = ONE_YEAR_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      } else if (_val === MembershipSelection.CurrentMember) {
        sku = oneMonthMembershipSku; // current member uses the one month SKU
        summaryKey = CURRENT_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      }
      dispatch({
        type: 'UPDATE_RADIO',
        payload: {
          value,
          sku,
          summaryKey,
          field: 'membership',
        },
      });
    } else if (
      Object.values(WarrantySelection).includes(value as WarrantySelection)
    ) {
      let sku = '';
      let summaryKey = ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      if (_val === WarrantySelection.TwoYear) {
        sku = twoYearWarrantySku;
        summaryKey = TWO_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      } else if (_val === WarrantySelection.ThreeYear) {
        sku = threeYearWarrantySku;
        summaryKey = THREE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      }
      dispatch({
        type: 'UPDATE_RADIO',
        payload: {
          value,
          sku,
          summaryKey,
          field: 'warranty',
        },
      });
    }
  };

  const handleFinishChange = (value: string): void => {
    dispatch({
      type: 'UPDATE_FINISH',
      payload: {
        value,
      },
    });
  };

  const handleSizeChange = (value: string): void => {
    dispatch({
      type: 'UPDATE_SIZE',
      payload: {
        value,
        summaryKey: SIZE_SELECTED_SUMMARY_KEY,
      },
    });
  };

  return {
    isSelectingSize,
    onFinishChange: handleFinishChange,
    onRadioChange: handleRadioChange,
    onSizeChange: handleSizeChange,
    order,
  };
};

export default useBuildRingOrder;
